import React from 'react';
import { Check, Copy } from 'lucide-react';

import { commaList } from './deliveredTaskHelpers';
import { useCopyFeedback } from './useCopyFeedback';

interface CopyOnClickProps {
  /** Exact text written to the clipboard; may be longer than what is shown. */
  value: string;
  /** What is being copied, for the tooltip and screen readers. */
  label: string;
  className?: string;
  children: React.ReactNode;
}

/** Shows `children`; clicking copies `value` and briefly confirms it. */
export function CopyOnClick({ value, label, className = '', children }: CopyOnClickProps) {
  const { copied, copy } = useCopyFeedback();
  return (
    <button
      type="button"
      onClick={() => void copy(value)}
      title={copied ? 'Copied' : `Click to copy ${label}: ${value}`}
      aria-label={copied ? `${label} copied` : `Copy ${label} ${value}`}
      className={`block max-w-full cursor-copy rounded text-left hover:bg-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${className}`}
    >
      {children}
      {copied && (
        <span className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-600" role="status">
          <Check className="h-3 w-3" />
          Copied
        </span>
      )}
    </button>
  );
}

/** Toolbar button that copies a list of IDs as one comma-separated string. */
export function CopyListButton({ ids, noun }: { ids: string[]; noun: string }) {
  const { copied, copy } = useCopyFeedback();
  const label = `Copy ${ids.length} ${noun}${ids.length === 1 ? '' : 's'}`;
  return (
    <button
      type="button"
      onClick={() => void copy(commaList(ids))}
      disabled={!ids.length}
      title={ids.length ? `Copies a comma-separated list of ${ids.length} ${noun}s` : `No ${noun}s to copy`}
      className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      <span role={copied ? 'status' : undefined}>{copied ? 'Copied' : label}</span>
    </button>
  );
}
